import React, { useState, useMemo, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Tooltip,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PermissionGate from "../../components/common/PermissionGate";
import {
  Add,
  Edit,
  Delete,
  Search,
  Visibility,
  Close,
  Download,
  Upload,
  Business,
  CloudUpload,
  CheckCircle,
  Error,
  Description,
  AccessTime,
  FileDownload,
  PersonAdd,
} from "@mui/icons-material";
import { useAllDepartments, useCreateDepartment, useUpdateDepartment, useDeleteDepartment, useUpdateDepartmentStatus } from "../../hooks/queries/useDepartments";
import Toast from "../../components/common/Toast";
import importService from "../../services/importService";
import departmentService from "../../services/departmentService";
import DataImportWizard from "../../components/common/DataImportWizard";
import StatusDropdown from "../../components/common/StatusDropdown";

// Validation schema for Add Department form
const departmentValidationSchema = Yup.object({
  name: Yup.string()
    .required("Department name is required")
    .min(3, "Department name must be at least 3 characters")
    .max(100, "Department name must not exceed 100 characters")
    .matches(/^[A-Za-z\s]+$/, "Department name must contain only letters and spaces")
    .trim(),
  code: Yup.string()
    .required("Department code is required")
    .min(2, "Department code must be at least 2 characters")
    .max(10, "Department code must not exceed 10 characters")
    .matches(/^[A-Z0-9]+$/, "Department code must contain only uppercase letters and numbers")
    .trim()
    .uppercase(),
  description: Yup.string()
    .max(500, "Description must not exceed 500 characters")
    .trim(),
  vision: Yup.string().max(1000, "Vision must not exceed 1000 characters").trim(),
  mission: Yup.string().max(1000, "Mission must not exceed 1000 characters").trim(),
});

const Departments = () => {
  console.log('Departments component rendered');
  
  const { user } = useSelector((state) => state.auth);
  const { selectedInstitute } = useSelector((state) => state.instituteContext);
  const queryClient = useQueryClient();

  // Normalize role: can be string or { slug }; "institute" is same as "admin" for this page
  const rawRole = user?.role?.slug || user?.role || "student";
  const roleStr = typeof rawRole === "string" ? rawRole.toLowerCase() : String(rawRole || "").toLowerCase();
  const userRole = roleStr === "institute" ? "admin" : roleStr;
  const needsInstituteContext = ["admin", "superadmin", "institute"].includes(userRole);
  const effectiveInstitute = userRole === "superadmin" ? selectedInstitute?.id : (selectedInstitute || user?.instituteId
    || null);
  const hasEffectiveInstitute = !!(effectiveInstitute ?? effectiveInstitute);
  
  // Get current user from localStorage
  const [currentUser] = useState(() => {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  });
  
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState([{ field: "id", sort: "desc" }]);
  

  // Debounce search query
  useEffect(() => {
    console.log("neeeed", needsInstituteContext)
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset to first page when search changes
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingDepartment, setViewingDepartment] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  const createDepartmentMutation = useCreateDepartment();
  const updateDepartmentMutation = useUpdateDepartment();
  const deleteDepartmentMutation = useDeleteDepartment();
  const updateStatusMutation = useUpdateDepartmentStatus();
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [togglingDepartmentId, setTogglingDepartmentId] = useState(null);
  const [optimisticStatus, setOptimisticStatus] = useState({});
  const [assignHodDialogOpen, setAssignHodDialogOpen] = useState(false);
  const [selectedDepartmentForHod, setSelectedDepartmentForHod] = useState(null);
  const [selectedHodUserId, setSelectedHodUserId] = useState("");
  const [departmentTeachers, setDepartmentTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  // Formik form for Add Department
  const formik = useFormik({
    initialValues: {
      name: "",
      code: "",
      description: "",
      vision: "",
      mission: "",
    },
    validationSchema: departmentValidationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // Send all fields to API (API may accept them or ignore extra fields)
        const payload = {
          name: values.name.trim(),
          code: values.code.trim(),
          description: values.description.trim() || undefined,
          vision: values.vision?.trim() || undefined,
          mission: values.mission?.trim() || undefined,
        };
       console.log("nnnnnn", needsInstituteContext)

        // Add instituteId for admin, superAdmin, and institute roles
        if (needsInstituteContext) {
          console.log("efffe", effectiveInstitute)
          const instituteId = effectiveInstitute
          console.log("efffe",  instituteId)

          if (instituteId) {
            payload.instituteId = instituteId;
          } else {
            setToastMessage("Please select an institute to manage departments.");
            setToastSeverity("error");
            setSubmitting(false);
            return;
          }
        }

        // mutateAsync resolves after onSuccess (including refetch) completes, so list is fresh when we close dialog
        await createDepartmentMutation.mutateAsync(payload);
        setToastMessage("Department created successfully!");
        setToastSeverity("success");
        setCreateDialogOpen(false);
        // Reset form with empty values - useEffect will also reset when dialog closes
        resetForm({ values: { name: "", code: "", description: "", vision: "", mission: "" } });
      } catch (error) {
        setToastMessage(
          error?.response?.data?.message || 
          error?.message || 
          "Failed to create department. Please try again."
        );
        setToastSeverity("error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Reset form when dialog closes
  useEffect(() => {
    if (!createDialogOpen) {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDialogOpen]);

  // Formik form for Edit Department
  const editFormik = useFormik({
    initialValues: {
      name: selectedDepartment?.name || "",
      code: selectedDepartment?.code || "",
      description: selectedDepartment?.description || "",
      vision: selectedDepartment?.vision || "",
      mission: selectedDepartment?.mission || "",
    },
    enableReinitialize: true,
    validateOnMount: false,
    validationSchema: departmentValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const departmentData = {
          name: values.name.trim(),
          code: values.code.trim(),
          description: values.description.trim() || undefined,
          vision: values.vision?.trim() || undefined,
          mission: values.mission?.trim() || undefined,
        };
          console.log(needsInstituteContext)
        // Add instituteId for admin, superAdmin, and institute roles
        if (needsInstituteContext) {
          const instituteId = effectiveInstitute
          if (instituteId) {
            departmentData.instituteId = instituteId;
          } else {
            setToastMessage("Please select an institute to manage departments.");
            setToastSeverity("error");
            return;
          }
        }
        

        await updateDepartmentMutation.mutateAsync({
          id: selectedDepartment?.id,
          departmentData,
        });
        setToastMessage("Department updated successfully!");
        setToastSeverity("success");
        setEditDialogOpen(false);
        setSelectedDepartment(null);
        // Mutation handles optimistic updates and refetching
      } catch (error) {
        setToastMessage(
          error?.response?.data?.message || 
          error?.message || 
          "Failed to update department. Please try again."
        );
        setToastSeverity("error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Reset edit form when dialog closes
  useEffect(() => {
    if (!editDialogOpen) {
      editFormik.resetForm();
      setSelectedDepartment(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDialogOpen]);

  // Handle edit button click — set form values and clear touched/errors before opening
  // so the edit form never shows validation errors for pre-filled fields on first open
  const handleEdit = (department) => {
    setSelectedDepartment(department);
    editFormik.setValues({
      name: department.name || "",
      code: department.code || "",
      description: department.description || "",
      vision: department.vision || "",
      mission: department.mission || "",
    });
    editFormik.setTouched({});
    editFormik.setErrors({});
    setEditDialogOpen(true);
  };

  // Handle view button click
  const handleView = (department) => {
    setViewingDepartment(department);
    setViewDialogOpen(true);
  };

  // Handle assign HOD button click
  const handleAssignHod = (department) => {
    setSelectedDepartmentForHod(department);
    setSelectedHodUserId(department.hodUserId || "");
    setAssignHodDialogOpen(true);
    fetchDepartmentTeachers(department.id);
  };

  // Fetch teachers for the department
  const fetchDepartmentTeachers = async (departmentId) => {
    setLoadingTeachers(true);
    try {
      const response = await departmentService.getDepartmentTeachers(departmentId);
      setDepartmentTeachers(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching department teachers:", error);
      setDepartmentTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  // Handle HOD assignment
  const handleAssignHodSubmit = async () => {
    if (!selectedDepartmentForHod) return;

    try {
      await departmentService.assignHOD(selectedDepartmentForHod.id, selectedHodUserId || null);
      setToastMessage("Department head assigned successfully!");
      setToastSeverity("success");
      setAssignHodDialogOpen(false);
      setSelectedDepartmentForHod(null);
      setSelectedHodUserId("");
      // Refresh departments data
      queryClient.invalidateQueries({ queryKey: ["departments", "all"] });
    } catch (error) {
      setToastMessage(
        error?.response?.data?.message || 
        error?.message || 
        "Failed to assign department head. Please try again."
      );
      setToastSeverity("error");
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingDepartment(null);
  };

  // Handle delete button click
  const handleDeleteClick = (department) => {
    setDepartmentToDelete(department);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!departmentToDelete) return;
    
    try {
      // mutateAsync resolves after onSuccess (including refetch) completes, so list is fresh when we close dialog
      await deleteDepartmentMutation.mutateAsync(departmentToDelete.id);
      setToastMessage("Department deleted successfully!");
      setToastSeverity("success");
      setDeleteDialogOpen(false);
      setDepartmentToDelete(null);
    } catch (error) {
      setToastMessage(
        error?.response?.data?.message || 
        error?.message || 
        "Failed to delete department. Please try again."
      );
      setToastSeverity("error");
    }
  };

  // Import states
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Component mount logging
  useEffect(() => {
    console.log('Departments component mounted');
    return () => {
      console.log('Departments component unmounted');
    };
  }, []);

  const handleExportDepartments = async () => {
    try {
      setToastMessage("Preparing export...");
      setToastSeverity("info");

      const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      const headers = [
        "Department Name",
        "Department Code",
        "Head of Department",
        "Teachers",
        "Students",
        "Courses",
        "Status",
        "Description",
      ];

      // Build CSV from normalized grid data to avoid column mismatch.
      const dataRows = departments.map((dept) => [
        escapeCsv(dept.name || ""),
        escapeCsv(dept.code || ""),
        escapeCsv(dept.head === "-" ? "" : dept.head || ""),
        escapeCsv(dept.teachers ?? 0),
        escapeCsv(dept.students ?? 0),
        escapeCsv(dept.courses ?? 0),
        escapeCsv(dept.statusString || (dept.status ? "active" : "inactive")),
        escapeCsv(dept.description || ""),
      ].join(","));

      const finalCsvContent = headers.join(",") + "\n" + dataRows.join("\n");
      
      // Create and download CSV file
      const blob = new Blob([finalCsvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `departments_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setToastMessage("Departments exported successfully!");
      setToastSeverity("success");
    } catch (error) {
      console.error('Export error:', error);
      setToastMessage(
        error?.response?.data?.message || 
        error?.message || 
        "Failed to export departments. Please try again."
      );
      setToastSeverity("error");
    }
  };

  // Handle import complete
  const handleImportComplete = () => {
    setToastMessage("Departments imported successfully!");
    setToastSeverity("success");
    setImportDialogOpen(false);
    
    // Refresh departments data to show newly imported departments
    queryClient.invalidateQueries({ queryKey: ["departments", "all"] });
  };

  // API call parameters
  const queryParams = useMemo(() => {
    const params = {
      sort: sortModel[0]?.field || "id",
      order: sortModel[0]?.sort?.toUpperCase() || "DESC",
      page: paginationModel.page + 1, // API uses 1-based pagination
      limit: paginationModel.pageSize,
    };
    
    if (debouncedSearchQuery) {
      params.search = debouncedSearchQuery;
    }

    if (statusFilter && statusFilter !== "all") {
      params.status = statusFilter;
    }

    // Add instituteId for admin, superAdmin, and institute roles
    if (needsInstituteContext && hasEffectiveInstitute) {
      params.instituteId = effectiveInstitute
    }
    
    console.log('Departments queryParams:', params);
    return params;
  }, [paginationModel, sortModel, debouncedSearchQuery, statusFilter, needsInstituteContext, hasEffectiveInstitute, effectiveInstitute]);

  // Fetch departments from API
  const { data: departmentsResponse, isLoading, isError, error, isFetching, status } = useAllDepartments(queryParams);

  // Debug logging
  useEffect(() => {
    console.log('Departments API Call State:', {
      queryParams,
      isLoading,
      isFetching,
      isError,
      status,
      error,
      response: departmentsResponse,
      hasData: !!departmentsResponse,
    });
  }, [queryParams, isLoading, isFetching, isError, error, departmentsResponse, status]);

  // Extract departments data from response
  const departments = useMemo(() => {
    if (!departmentsResponse) {
      if (process.env.NODE_ENV === 'development') {
        console.log('No departmentsResponse');
      }
      return [];
    }
    
    let departmentsArray = [];
    
    // Handle different response structures
    // Case 1: Response is directly an array
    if (Array.isArray(departmentsResponse)) {
      departmentsArray = departmentsResponse;
    }
    // Case 2: Response has structure { status, message, data, pagination }
    else if (departmentsResponse.data && Array.isArray(departmentsResponse.data)) {
      departmentsArray = departmentsResponse.data;
    }
    // Case 3: Response has departments array
    else if (departmentsResponse.departments && Array.isArray(departmentsResponse.departments)) {
      departmentsArray = departmentsResponse.departments;
    }
    // Case 4: Response has rows array (common in paginated responses)
    else if (departmentsResponse.rows && Array.isArray(departmentsResponse.rows)) {
      departmentsArray = departmentsResponse.rows;
    }
    
    console.log('Departments array extracted:', {
      departmentsArrayLength: departmentsArray.length,
      firstDept: departmentsArray[0],
      fullResponse: departmentsResponse,
      fullResponseStringified: JSON.stringify(departmentsResponse, null, 2),
      responseDataType: typeof departmentsResponse,
      hasData: !!departmentsResponse?.data,
      dataIsArray: Array.isArray(departmentsResponse?.data),
      dataType: typeof departmentsResponse?.data,
      responseKeys: departmentsResponse ? Object.keys(departmentsResponse) : [],
      responseDataValue: departmentsResponse?.data,
      responseDataStringified: departmentsResponse?.data ? JSON.stringify(departmentsResponse.data, null, 2) : 'no data',
    });
    
    // Map the departments array to the format expected by the component
    const mapped = departmentsArray.map((dept) => ({
      id: dept.id || dept._id,
      name: dept.name || dept.departmentName || "-",
      code: dept.code || dept.departmentCode || (dept.name ? dept.name.substring(0, 3).toUpperCase() : "-"),
      head: dept.hodName || dept.headOfDepartment || dept.hod || "-",
      teachers: dept.teachersCount || dept.teachers || dept.totalTeachers || 0,
      students: dept.studentsCount || dept.students || dept.totalStudents || 0,
      courses: dept.courses || dept.courseCount || dept.coursesCount || dept.totalCourses || 0,
      status: typeof dept.status === 'boolean' 
        ? dept.status
        : (dept.status === "active" || dept.status === true || dept.isActive === true),
      statusString: typeof dept.status === 'boolean' 
        ? (dept.status ? "active" : "inactive")
        : (dept.status || (dept.isActive ? "active" : "inactive")),
      description: dept.description || "",
      vision: dept.vision || "",
      mission: dept.mission || "",
      duration: dept.duration || 0,
      canDelete: dept.canDelete !== undefined ? dept.canDelete : true,
    }));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Mapped departments:', mapped);
    }
    
    return mapped;
  }, [departmentsResponse]);

  // Get total count for pagination
  const totalCount = useMemo(() => {
    if (departmentsResponse?.pagination?.total) {
      return departmentsResponse.pagination.total;
    }
    if (departmentsResponse?.total) {
      return departmentsResponse.total;
    }
    if (departmentsResponse?.totalCount) {
      return departmentsResponse.totalCount;
    }
    if (departmentsResponse?.data?.total) {
      return departmentsResponse.data.total;
    }
    if (departmentsResponse?.data?.totalCount) {
      return departmentsResponse.data.totalCount;
    }
    if (Array.isArray(departmentsResponse)) {
      return departmentsResponse.length;
    }
    // Fallback to current departments length if no pagination info
    return departments.length;
  }, [departmentsResponse, departments.length]);

  // Extract totals from API response
  const totals = useMemo(() => {
    return {
      totalDepartments: departmentsResponse?.totals?.totalDepartments || 
                        departmentsResponse?.data?.totals?.totalDepartments || 
                        0,
      activeDepartments: departmentsResponse?.totals?.activeDepartments || 
                         departmentsResponse?.data?.totals?.activeDepartments || 
                         0,
      totalTeachers: departmentsResponse?.totals?.totalTeachers || 
                     departmentsResponse?.data?.totals?.totalTeachers || 
                     0,
      totalStudents: departmentsResponse?.totals?.totalStudents || 
                     departmentsResponse?.data?.totals?.totalStudents || 
                     0,
    };
  }, [departmentsResponse]);

  const handleStatusToggle = (row, newStatus) => {
    const id = row?.id;
    if (id == null) return;
    const canDelete = row.canDelete !== undefined ? row.canDelete : true;
    if (!canDelete) return;
    setTogglingDepartmentId(id);
    setOptimisticStatus((prev) => ({ ...prev, [id]: newStatus }));
    updateStatusMutation.mutate(
      { id, status: newStatus },
      {
        onSuccess: () => {
          setToastMessage(
            `Department ${newStatus ? "activated" : "deactivated"} successfully!`
          );
          setToastSeverity("success");
        },
        onError: (error) => {
          setOptimisticStatus((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          setToastMessage(
            error?.response?.data?.message ||
            error?.message ||
            "Failed to update department status. Please try again."
          );
          setToastSeverity("error");
        },
        onSettled: () => {
          setTogglingDepartmentId(null);
          setOptimisticStatus((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        },
      }
    );
  };

  const columns = [
    {
      field: "name",
      headerName: "Department",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "primary.light", width: 40, height: 40 }}>
            <Business />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.code}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "head", headerName: "Head", width: 180, sortable: false },
    { field: "teachers", headerName: "Teachers", width: 100 },
    { field: "students", headerName: "Students", width: 100 },
    { field: "courses", headerName: "Courses", width: 100 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const rowId = params.row?.id;
        if (rowId == null) return null;
        const resolvedActive = params.row.status === true || params.row.status === "active";
        const isActive = optimisticStatus[rowId] ?? resolvedActive;
        const canDelete = params.row.canDelete !== undefined ? params.row.canDelete : true;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} key={rowId}>
            <Switch
              key={rowId}
              checked={!!isActive}
              onChange={(e) => handleStatusToggle(params.row, e.target.checked)}
              disabled={togglingDepartmentId === rowId || !canDelete}
              size="small"
              color="success"
            />
            <Chip
              label={isActive ? "active" : "inactive"}
              size="small"
              color={isActive ? "success" : "default"}
            />
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton 
              size="small"
              onClick={() => handleView(params.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <PermissionGate permission="department.update">
            <Tooltip title="Assign Head">
              <IconButton size="small" onClick={() => handleAssignHod(params.row)} color="secondary">
                <PersonAdd />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
                <Edit />
              </IconButton>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission="department.delete">
            <Tooltip title={params.row.canDelete ? "Delete" : "Cannot delete this department"}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(params.row)}
                  disabled={!params.row.canDelete}
                  sx={{
                    opacity: params.row.canDelete ? 1 : 0.5,
                    cursor: params.row.canDelete ? "pointer" : "not-allowed",
                  }}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </PermissionGate>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* {needsInstituteContext && !hasEffectiveInstitute && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Please select an institute to manage departments.
        </Alert>
      )} */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Department Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all departments in the institute
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGate permission="department.import">
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
          </PermissionGate>
          <PermissionGate permission="department.export">
            <Button 
              variant="outlined" 
              startIcon={<Download />}
              onClick={handleExportDepartments}
              disabled={departments.length === 0}
            >
              Export
            </Button>
          </PermissionGate>
          <PermissionGate permission="department.create">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Add Department
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Departments",
            value: isLoading ? "-" : totals.totalDepartments || totalCount || departments.length,
            color: "primary.main",
          },
          {
            label: "Active",
            value: isLoading ? "-" : totals.activeDepartments || departments.filter((d) => d.status === true || d.status === "active").length,
            color: "success.main",
          },
          {
            label: "Total Teachers",
            value: isLoading ? "-" : totals.totalTeachers || departments.reduce((a, d) => a + (d.teachers || 0), 0),
            color: "info.main",
          },
          {
            label: "Total Students",
            value: isLoading ? "-" : totals.totalStudents || departments.reduce((a, d) => a + (d.students || 0), 0),
            color: "warning.main",
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              placeholder="Search departments..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <StatusDropdown
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              label="Status"
              size="small"
              sx={{ ml: 2, minWidth: 160 }}
            />
          </Box>

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error?.response?.data?.message || error?.message || "Failed to load departments. Please try again."}
            </Alert>
          )}

          {isLoading && departments.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={departments}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              rowCount={totalCount}
              paginationMode="server"
              sortingMode="server"
              loading={isLoading}
              pagination={true}
              sx={{ 
                border: "none",
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid rgba(224, 224, 224, 1)",
                  minHeight: "52px",
                },
                "& .MuiDataGrid-pagination": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                },
                "& .MuiTablePagination-root": {
                  width: "100%",
                },
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: "16px",
                  paddingRight: "16px",
                },
                "& .MuiTablePagination-spacer": {
                  flex: "1 1 100%",
                },
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseViewDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Department Details</Typography>
            <IconButton onClick={handleCloseViewDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewingDepartment ? (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingDepartment.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Code
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingDepartment.code}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Head of Department
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingDepartment.head || "Not assigned"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={viewingDepartment.status === true || viewingDepartment.status === "active" ? "Active" : "Inactive"}
                  color={viewingDepartment.status === true || viewingDepartment.status === "active" ? "success" : "default"}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Teachers
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingDepartment.teachers ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Students
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingDepartment.students ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Courses
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingDepartment.courses ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                  {viewingDepartment.description || "No description provided"}
                </Typography>
              </Grid>
              {viewingDepartment.vision && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Vision
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                    {viewingDepartment.vision}
                  </Typography>
                </Grid>
              )}
              {viewingDepartment.mission && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Mission
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                    {viewingDepartment.mission}
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={32} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setCreateDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              Add New Department
              <IconButton onClick={() => setCreateDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  required
                  label="Department Name"
                  name="name"
                  value={formik.values.name}
                  onKeyPress={(e) => {
                    // Prevent numbers from being typed
                    if (/\d/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    // Filter out numbers from the input value
                    const filteredValue = e.target.value.replace(/\d/g, '');
                    formik.setFieldValue('name', filteredValue);
                    // Auto-generate code from name if code is empty
                    if (!formik.values.code && filteredValue.length >= 2) {
                      const autoCode = filteredValue
                        .substring(0, 3)
                        .toUpperCase()
                        .replace(/[^A-Z]/g, '');
                      if (autoCode.length >= 2) {
                        formik.setFieldValue('code', autoCode);
                      }
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder="e.g., Computer Science"
                  autoFocus
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  required
                  label="Code"
                  name="code"
                  value={formik.values.code}
                  onChange={(e) => {
                    // Convert to uppercase automatically
                    const upperValue = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    formik.setFieldValue('code', upperValue);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.code && Boolean(formik.errors.code)}
                  helperText={formik.touched.code && formik.errors.code || "e.g., CS, ECE, ME"}
                  placeholder="CS"
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description || "Optional description of the department"}
                  placeholder="Brief description of the department..."
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vision"
                  name="vision"
                  multiline
                  rows={2}
                  value={formik.values.vision}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.vision && Boolean(formik.errors.vision)}
                  helperText={formik.touched.vision && formik.errors.vision || "Department vision statement (optional)"}
                  placeholder="e.g., To be a center of excellence..."
                  inputProps={{ maxLength: 1000 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mission"
                  name="mission"
                  multiline
                  rows={2}
                  value={formik.values.mission}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.mission && Boolean(formik.errors.mission)}
                  helperText={formik.touched.mission && formik.errors.mission || "Department mission statement (optional)"}
                  placeholder="e.g., To provide quality education..."
                  inputProps={{ maxLength: 1000 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setCreateDialogOpen(false)}
              disabled={createDepartmentMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={createDepartmentMutation.isPending || !formik.isValid}
              startIcon={createDepartmentMutation.isPending ? <CircularProgress size={16} /> : <Add />}
            >
              {createDepartmentMutation.isPending ? "Creating..." : "Create Department"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Assign HOD Dialog */}
      <Dialog
        open={assignHodDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setAssignHodDialogOpen(false);
          setSelectedDepartmentForHod(null);
          setSelectedHodUserId("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            Assign Department Head
            <IconButton onClick={() => {
              setAssignHodDialogOpen(false);
              setSelectedDepartmentForHod(null);
              setSelectedHodUserId("");
            }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a teacher from <b>{selectedDepartmentForHod?.name}</b> to be the new Head of Department.
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Head of Department</InputLabel>
            <Select
              value={selectedHodUserId}
              label="Select Head of Department"
              onChange={(e) => setSelectedHodUserId(e.target.value)}
              disabled={loadingTeachers}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {loadingTeachers ? (
                <MenuItem disabled>Loading teachers...</MenuItem>
              ) : departmentTeachers.length > 0 ? (
                departmentTeachers.map((t) => (
                  <MenuItem key={t.id} value={t.userId}>
                    {t.user?.fullName || t.name || `Teacher ${t.id}`}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No teachers available in this department</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => {
            setAssignHodDialogOpen(false);
            setSelectedDepartmentForHod(null);
            setSelectedHodUserId("");
          }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleAssignHodSubmit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setEditDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={editFormik.handleSubmit}>
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              Edit Department
              <IconButton onClick={() => setEditDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  required
                  label="Department Name"
                  name="name"
                  value={editFormik.values.name}
                  onKeyPress={(e) => {
                    // Prevent numbers from being typed
                    if (/\d/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    // Filter out numbers from the input value
                    const filteredValue = e.target.value.replace(/\d/g, '');
                    editFormik.setFieldValue('name', filteredValue);
                  }}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.name && Boolean(editFormik.errors.name)}
                  helperText={editFormik.touched.name && editFormik.errors.name}
                  placeholder="e.g., Computer Science"
                  autoFocus
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  required
                  label="Code"
                  name="code"
                  value={editFormik.values.code}
                  onChange={(e) => {
                    // Convert to uppercase automatically
                    const upperValue = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    editFormik.setFieldValue('code', upperValue);
                  }}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.code && Boolean(editFormik.errors.code)}
                  helperText={editFormik.touched.code && editFormik.errors.code || "e.g., CS, ECE, ME"}
                  placeholder="CS"
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={editFormik.values.description}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.description && Boolean(editFormik.errors.description)}
                  helperText={editFormik.touched.description && editFormik.errors.description || "Optional description of the department"}
                  placeholder="Brief description of the department..."
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vision"
                  name="vision"
                  multiline
                  rows={2}
                  value={editFormik.values.vision}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.vision && Boolean(editFormik.errors.vision)}
                  helperText={editFormik.touched.vision && editFormik.errors.vision || "Department vision statement"}
                  placeholder="e.g., To be a center of excellence..."
                  inputProps={{ maxLength: 1000 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mission"
                  name="mission"
                  multiline
                  rows={2}
                  value={editFormik.values.mission}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.mission && Boolean(editFormik.errors.mission)}
                  helperText={editFormik.touched.mission && editFormik.errors.mission || "Department mission statement"}
                  placeholder="e.g., To provide quality education..."
                  inputProps={{ maxLength: 1000 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setEditDialogOpen(false)}
              disabled={updateDepartmentMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={updateDepartmentMutation.isPending || !editFormik.isValid}
              startIcon={updateDepartmentMutation.isPending ? <CircularProgress size={16} /> : <Edit />}
            >
              {updateDepartmentMutation.isPending ? "Updating..." : "Update Department"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Import Wizard */}
      <DataImportWizard
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        entityType="departments"
        instituteId={currentUser?.instituteId}
        onImportComplete={handleImportComplete}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setDeleteDialogOpen(false);
          setDepartmentToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Delete Department</Typography>
            <IconButton onClick={() => {
              setDeleteDialogOpen(false);
              setDepartmentToDelete(null);
            }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete this department? This action cannot be undone.
          </Alert>
          {departmentToDelete && (
            <Box>
              <Typography variant="body1" fontWeight={500} gutterBottom>
                Department: {departmentToDelete.name}
              </Typography>
              {departmentToDelete.code && (
                <Typography variant="body2" color="text.secondary">
                  Code: {departmentToDelete.code}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setDepartmentToDelete(null);
            }}
            disabled={deleteDepartmentMutation.isPending}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteDepartmentMutation.isPending}
            startIcon={deleteDepartmentMutation.isPending ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleteDepartmentMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
};

export default Departments;
